import { useMe } from "@/api/services/main/auth";
import {
  createConversationApi,
  createMessageApi,
  deleteMessageApi,
  setReadMessageApi,
  useConversation,
  useMessageListByConversation,
} from "@/api/services/main/conversation";
import { TGetMessageOutputDto } from "@/api/services/main/conversation/dtos/get-message-list-by-conversation.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CHANNELS, EVENTS } from "@/constants/socket";
import { TMessageBoxProps } from "@/features/chat/components/MessageBox";
import { useSocket } from "@/features/chat/providers/SocketProvider";
import { toNumber } from "@/utils/number";
import { isFireEvent } from "@/utils/pieces";
import { replaceStr } from "@/utils/string";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

export const useRoom = () => {
  const { socket } = useSocket();
  const { data: meResult } = useMe();
  const { data: conversationResult } = useConversation();
  const roomData = conversationResult?.data ? conversationResult?.data : null;
  const queryClient = useQueryClient();

  const roomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (roomRef.current) {
      roomRef.current.scrollTop = roomRef.current.scrollHeight;
    }
  };

  const {
    dataFormatter: messages,
    hasNextPage,
    isFetching,
    fetchNextPage,
  } = useMessageListByConversation({ conversationId: roomData?.id?.toString() ?? "", enabled: roomData?.id ? true : false });

  const [oldMessages, setOldMessages] = useState<TMessageBoxProps[]>([]);
  const [newMessages, setNewMessages] = useState<TMessageBoxProps[]>([]);

  useEffect(() => {
    const fetchOldMessages = async () => {
      const oldMsgs =
        messages && Array.isArray(messages) && messages.length > 0
          ? await Promise.all(
              messages.reverse().flatMap(async (message) => {
                return formatMsgObj(message);
              }),
            )
          : roomData?.messages && Array.isArray(roomData?.messages) && roomData?.messages.length > 0
            ? await Promise.all(
                roomData?.messages.reverse().flatMap(async (message) => {
                  return formatMsgObj(message);
                }),
              )
            : [];

      setOldMessages(oldMsgs);
    };
    fetchOldMessages();
  }, [messages, roomData]);

  useEffect(() => {
    if (socket && roomData?.id) {
      socket.emit(EVENTS.CONVERSATION.JOIN_ROOM, replaceStr(CHANNELS.CONVERSATION.ROOM, { conversationId: roomData.id }));
      socket.on(EVENTS.CONVERSATION.READ_MESSAGE, (data) => {
        if (meResult?.data.id && data.readUserId != meResult.data.id) {
          setOldMessages((prevMessages) => prevMessages.map((msg) => (msg.position === "right" ? { ...msg, status: "received" } : msg)));

          setNewMessages((prevMessages) => prevMessages.map((msg) => (msg.position === "right" ? { ...msg, status: "received" } : msg)));
        }
      });
      socket.on(EVENTS.CONVERSATION.DELETE_MESSAGE, (data) => {
        setOldMessages((prevMessages) => prevMessages.filter((msg) => toNumber(msg.id) !== toNumber(data.messageId)));
        setNewMessages((prevMessages) => prevMessages.filter((msg) => toNumber(msg.id) !== toNumber(data.messageId)));
      });
      return () => {
        socket.emit(EVENTS.CONVERSATION.LEAVE, replaceStr(CHANNELS.CONVERSATION.ROOM, { conversationId: roomData.id }));
      };
    }
  }, [socket, roomData]);

  useEffect(() => {
    const callback = async (e: Event) => {
      if (isFireEvent<TGetMessageOutputDto>(e)) {
        const { detail: receivedMessage } = e;
        if (roomData && receivedMessage.conversationId?.toString() === roomData.id?.toString()) {
          let newMessage: TMessageBoxProps[] = [];
          newMessage = [await formatMsgObj(receivedMessage)];
          setNewMessages((cur) => [...cur, ...newMessage]);
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
      }
    };
    window.addEventListener(EVENTS.CONVERSATION.NEW_MESSAGE, callback);
    return () => {
      window.removeEventListener(EVENTS.CONVERSATION.NEW_MESSAGE, callback);
    };
  }, [meResult, roomData]);

  const formatMsgObj = useCallback(
    async (msgObj: TGetMessageOutputDto): Promise<TMessageBoxProps> => {
      const isMyMsg = meResult?.data.id == msgObj.creator?.id;

      const msg = {} as TMessageBoxProps;
      msg.title = isMyMsg ? "" : msgObj.creator?.fullName;
      msg.messageText = msgObj.text;
      msg.position = isMyMsg ? "right" : "left";
      msg.statusTitle = dayjs(msgObj.createdAt).format("HH:mm");
      msg.id = msgObj.id;
      msg.readUserIds = msgObj.readUserIds;
      msg.createdAt = msgObj.createdAt;

      msg.files = msgObj.files;

      if (isMyMsg) {
        if (meResult?.data.id && msgObj.readUserIds && msgObj.readUserIds.length > 0 && !msgObj.readUserIds.includes(meResult?.data.id)) {
          msg.status = "received";
        } else {
          msg.status = "sent";
        }
      } else {
        if (meResult?.data.id && msgObj.readUserIds && msgObj.readUserIds.length > 0 && msgObj.readUserIds.includes(meResult?.data.id)) {
          msg.status = "received";
        } else {
          msg.status = "sent";
        }
      }

      return msg;
    },
    [meResult?.data.id],
  );

  const sendMsg = async (msg: string, filePaths: string[]) => {
    if (!roomData) {
      await createConversationApi({
        message: {
          text: msg,
          filePaths,
        },
      });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONVERSATION.FETCH_DETAIL] });
      return;
    }
    await createMessageApi(roomData.id?.toString(), { text: msg, filePaths });
    scrollToBottom();
  };

  const readMsg = async () => {
    const oldMessageId = oldMessages.length > 0 ? oldMessages[oldMessages.length - 1].id?.toString() : null;
    if (roomData && oldMessageId) {
      await setReadMessageApi({ conversationId: roomData.id?.toString(), messageId: oldMessageId });
    }
  };
  const deleteMsg = async (id: number) => {
    if (roomData) {
      await deleteMessageApi({ conversationId: roomData.id?.toString(), messageId: id.toString() });
    }
  };

  return { oldMessages, newMessages, roomData, hasNextPage, isFetching, fetchNextPage, sendMsg, readMsg, deleteMsg, roomRef, scrollToBottom };
};
